<div class="wb-page">

  <!-- Header -->
  <div class="wb-header">
    <div class="wb-title">
      <span class="material-icons">graphic_eq</span>
      <div>
        <h2>Whisper Benchmark</h2>
        <p>Test <strong>{{ workerHealth?.model || 'large-v3' }}</strong> — upload or record audio, measure transcription timing</p>
      </div>
    </div>
    <div class="health-badge" [class.up]="workerHealth?.status === 'ok'" [class.down]="workerHealth?.status !== 'ok' && !healthLoading">
      <span class="material-icons">{{ workerHealth?.status === 'ok' ? 'check_circle' : 'error_outline' }}</span>
      <span>{{ healthLoading ? 'Checking...' : workerHealth?.status === 'ok' ? 'Worker Online' : 'Worker Offline' }}</span>
      <button class="icon-btn" (click)="checkHealth()" title="Refresh health">
        <span class="material-icons">refresh</span>
      </button>
    </div>
  </div>

  <!-- Worker Health Detail -->
  <div class="health-detail" *ngIf="workerHealth && !healthLoading">
    <span *ngIf="workerHealth.model">Model: <strong>{{ workerHealth.model }}</strong></span>
    <span *ngIf="workerHealth.model_load_ms">Load time: <strong>{{ fmt(workerHealth.model_load_ms) }}</strong></span>
    <span *ngIf="workerHealth.error" class="error-text">{{ workerHealth.error }}</span>
  </div>

  <!-- Input Panel -->
  <div class="wb-card">
    <h3>New Benchmark</h3>

    <div class="form-row">
      <div class="form-field">
        <label for="wb-test-name">Test Name (optional)</label>
        <input id="wb-test-name" type="text" [(ngModel)]="testName" placeholder="e.g. Urdu complaint recording" [disabled]="state === 'running'" />
      </div>
      <div class="form-field narrow">
        <label for="wb-language">Language</label>
        <select id="wb-language" [(ngModel)]="language" [disabled]="state === 'running'">
          <option value="ur">Urdu (ur)</option>
          <option value="en">English (en)</option>
          <option value="">Auto-detect</option>
        </select>
      </div>
    </div>

    <!-- Audio Source -->
    <div class="audio-source">
      <div class="source-tabs">
        <button class="tab-btn" [class.active]="!useRecording" (click)="useRecording = false; clear(false)">
          <span class="material-icons">upload_file</span> Upload File
        </button>
        <button class="tab-btn" [class.active]="useRecording" (click)="useRecording = true">
          <span class="material-icons">mic</span> Record Audio
        </button>
      </div>

      <!-- File Upload -->
      <div class="upload-zone" *ngIf="!useRecording">
        <label class="upload-label" for="wb-audio-file">
          <span class="material-icons">audio_file</span>
          <span>{{ selectedFile ? selectedFile.name : 'Click to select audio file (WAV, MP3, WebM, OGG)' }}</span>
          <input id="wb-audio-file" type="file" accept="audio/*" (change)="onFileSelected($event)" [disabled]="state === 'running'" style="display:none" />
        </label>
        <p class="file-meta" *ngIf="selectedFile">
          Size: {{ fmtSize(selectedFile.size) }} &nbsp;|&nbsp; Type: {{ selectedFile.type || 'unknown' }}
        </p>
        <div class="audio-preview" *ngIf="uploadedAudioUrl">
          <audio [src]="uploadedAudioUrl" controls></audio>
        </div>
      </div>

      <!-- Recorder -->
      <div class="recorder-zone" *ngIf="useRecording">
        <div class="recorder-controls">
          <button class="rec-btn start" (click)="startRecording()" [disabled]="!canStart || state === 'running'">
            <span class="material-icons">fiber_manual_record</span> Record
          </button>
          <button class="rec-btn stop" (click)="stopRecording()" [disabled]="!canStop">
            <span class="material-icons">stop</span> Stop
          </button>
          <div class="rec-timer" [class.active]="state === 'recording'">
            <span class="material-icons pulse-dot" *ngIf="state === 'recording'">circle</span>
            {{ formattedDuration }}
          </div>
        </div>
        <div class="audio-preview" *ngIf="audioUrl">
          <audio [src]="audioUrl" controls></audio>
          <span class="file-meta">{{ fmtSize(fileSize) }}</span>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div class="error-bar" *ngIf="errorMessage">
      <span class="material-icons">warning</span> {{ errorMessage }}
    </div>

    <!-- Run Button -->
    <div class="run-row">
      <button class="run-btn" (click)="runBenchmark()" [disabled]="!canRun || state === 'running'">
        <span class="material-icons" *ngIf="state !== 'running'">play_arrow</span>
        <span class="spinner" *ngIf="state === 'running'"></span>
        {{ state === 'running' ? 'Running benchmark...' : 'Run Benchmark' }}
      </button>
      <button class="clear-btn" (click)="clear()" [disabled]="state === 'running'">
        <span class="material-icons">clear</span> Clear
      </button>
    </div>
  </div>

  <!-- Results Panel -->
  <div class="wb-card results-card" *ngIf="result">
    <h3>
      <span class="material-icons" [class.ok]="result.status === 'SUCCESS'" [class.fail]="result.status !== 'SUCCESS'">
        {{ result.status === 'SUCCESS' ? 'check_circle' : 'error' }}
      </span>
      Result — {{ result.testName || result.audioFilename || 'Benchmark' }}
    </h3>

    <!-- Audio Player -->
    <div class="result-audio-player" *ngIf="activeAudioUrl">
      <span class="section-label">Play Audio</span>
      <audio [src]="activeAudioUrl" controls></audio>
    </div>

    <!-- Extracted Fields -->
    <div class="extracted-fields" *ngIf="result.extractedName || result.extractedMobile || result.extractedCnic">
      <span class="section-label">Extracted Information</span>
      <div class="extracted-grid">
        <div class="extracted-cell" *ngIf="result.extractedName">
          <span class="material-icons">person</span>
          <div>
            <span class="extracted-label">Name</span>
            <span class="extracted-value">{{ result.extractedName }}</span>
          </div>
        </div>
        <div class="extracted-cell" *ngIf="result.extractedMobile">
          <span class="material-icons">phone</span>
          <div>
            <span class="extracted-label">Mobile</span>
            <span class="extracted-value">{{ result.extractedMobile }}</span>
          </div>
        </div>
        <div class="extracted-cell" *ngIf="result.extractedCnic">
          <span class="material-icons">badge</span>
          <div>
            <span class="extracted-label">CNIC</span>
            <span class="extracted-value">{{ result.extractedCnic }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="extracted-missing" *ngIf="result.status === 'SUCCESS' && !result.extractedName && !result.extractedMobile && !result.extractedCnic">
      <span class="material-icons">info_outline</span>
      No name, mobile, or CNIC detected in transcription
    </div>

    <!-- Transcription -->
    <div class="transcription-box" *ngIf="result.transcription">
      <span class="section-label">Transcription</span>
      <div class="transcription-text" dir="auto">{{ result.transcription }}</div>
    </div>

    <div class="error-bar" *ngIf="result.errorMessage">
      <span class="material-icons">warning</span> {{ result.errorMessage }}
    </div>

    <!-- Metadata grid -->
    <div class="meta-grid">
      <div class="meta-cell"><span class="meta-label">Language</span><span>{{ result.language || '-' }}</span></div>
      <div class="meta-cell"><span class="meta-label">Audio Duration</span><span>{{ fmtDuration(result.audioDurationS) }}</span></div>
      <div class="meta-cell"><span class="meta-label">Audio Size</span><span>{{ fmtSize(result.audioSizeBytes) }}</span></div>
      <div class="meta-cell"><span class="meta-label">Word Count</span><span>{{ result.wordCount ?? '-' }}</span></div>
      <div class="meta-cell"><span class="meta-label">Segments</span><span>{{ result.segmentCount ?? '-' }}</span></div>
      <div class="meta-cell"><span class="meta-label">Model</span><span class="model-label">{{ result.modelUsed }}</span></div>
    </div>

    <!-- Timing Breakdown -->
    <div class="timing-section">
      <h4>Timing Breakdown</h4>
      <div class="timing-total">Total: <strong>{{ fmt(result.totalMs) }}</strong></div>
      <div class="timing-bars">
        <div class="timing-row">
          <span class="timing-label">Model Load</span>
          <div class="bar-track">
            <div class="bar model-load" [style.width.%]="msBar(result.modelLoadMs, result.totalMs)"></div>
          </div>
          <span class="timing-value">{{ fmt(result.modelLoadMs) }}</span>
        </div>
        <div class="timing-row">
          <span class="timing-label">Transcribe</span>
          <div class="bar-track">
            <div class="bar transcribe" [style.width.%]="msBar(result.transcribeMs, result.totalMs)"></div>
          </div>
          <span class="timing-value">{{ fmt(result.transcribeMs) }}</span>
        </div>
        <div class="timing-row">
          <span class="timing-label">Alignment</span>
          <div class="bar-track">
            <div class="bar align" [style.width.%]="msBar(result.alignMs, result.totalMs)"></div>
          </div>
          <span class="timing-value">{{ fmt(result.alignMs) }}</span>
        </div>
      </div>
    </div>

    <!-- Segments -->
    <div class="segments-section" *ngIf="result.segments?.length">
      <h4>Word-level Segments ({{ result.segments!.length }})</h4>
      <div class="segments-table-wrap">
        <table class="segments-table">
          <thead><tr><th>#</th><th>Start</th><th>End</th><th>Text</th></tr></thead>
          <tbody>
            <tr *ngFor="let seg of result.segments; let i = index">
              <td>{{ i + 1 }}</td>
              <td>{{ seg.start?.toFixed(2) }}s</td>
              <td>{{ seg.end?.toFixed(2) }}s</td>
              <td dir="auto">{{ seg.text }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- History Table -->
  <div class="wb-card history-card">
    <div class="history-header">
      <h3><span class="material-icons">history</span> Benchmark History</h3>
      <div class="history-meta">{{ historyTotal }} records total</div>
    </div>

    <div class="loading-bar" *ngIf="historyLoading">
      <span class="spinner"></span> Loading...
    </div>

    <div class="no-data" *ngIf="!historyLoading && history.length === 0">
      No benchmarks yet. Run your first test above.
    </div>

    <div class="history-table-wrap" *ngIf="!historyLoading && history.length > 0">
      <table class="history-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Test Name</th>
            <th>File</th>
            <th>Duration</th>
            <th>Total Time</th>
            <th>Words</th>
            <th>Language</th>
            <th>Status</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let rec of history">
            <tr class="history-row" [class.failed]="rec.status !== 'SUCCESS'">
              <td>{{ rec.id }}</td>
              <td>{{ rec.testName || '-' }}</td>
              <td class="filename-cell" [title]="rec.audioFilename || ''">{{ rec.audioFilename || '-' }}</td>
              <td>{{ fmtDuration(rec.audioDurationS) }}</td>
              <td><strong>{{ fmt(rec.totalMs) }}</strong></td>
              <td>{{ rec.wordCount ?? '-' }}</td>
              <td>{{ rec.language || '-' }}</td>
              <td><span class="status-chip" [class.ok]="rec.status === 'SUCCESS'" [class.fail]="rec.status !== 'SUCCESS'">{{ rec.status }}</span></td>
              <td>{{ fmtDate(rec.createdAt) }}</td>
              <td class="actions-cell">
                <button class="icon-btn" (click)="loadSegments(rec)" [title]="rec._expanded ? 'Collapse' : 'Expand'">
                  <span class="material-icons">{{ rec._expanded ? 'expand_less' : 'expand_more' }}</span>
                </button>
                <button class="icon-btn danger" (click)="deleteRecord(rec)" title="Delete">
                  <span class="material-icons">delete</span>
                </button>
              </td>
            </tr>
            <!-- Expanded row -->
            <tr class="expand-row" *ngIf="rec._expanded">
              <td colspan="10">
                <div class="expand-content">
                  <div class="transcription-text" dir="auto" *ngIf="rec.transcription">{{ rec.transcription }}</div>
                  <div class="timing-inline">
                    <span>Model Load: <strong>{{ fmt(rec.modelLoadMs) }}</strong></span>
                    <span>Transcribe: <strong>{{ fmt(rec.transcribeMs) }}</strong></span>
                    <span>Align: <strong>{{ fmt(rec.alignMs) }}</strong></span>
                  </div>
                  <div *ngIf="rec.segments?.length" class="segments-mini">
                    <div *ngFor="let seg of rec.segments" class="seg-chip" dir="auto">
                      <span class="seg-time">{{ seg.start?.toFixed(1) }}s</span> {{ seg.text }}
                    </div>
                  </div>
                  <div *ngIf="rec.errorMessage" class="error-text">{{ rec.errorMessage }}</div>
                </div>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pagination" *ngIf="historyTotal > 20">
      <button [disabled]="historyPage === 0" (click)="loadHistory(historyPage - 1)">
        <span class="material-icons">chevron_left</span>
      </button>
      <span>Page {{ historyPage + 1 }} of {{ totalHistoryPages }}</span>
      <button [disabled]="(historyPage + 1) * 20 >= historyTotal" (click)="loadHistory(historyPage + 1)">
        <span class="material-icons">chevron_right</span>
      </button>
    </div>
  </div>

</div>
